import {
  IconAddressBook,
  IconAdjustments,
  IconArrowsRight,
  IconBellRinging,
  IconBook2,
  IconBuilding,
  IconBuildingSkyscraper,
  IconBuildingStore,
  IconCalendar,
  IconChartBar,
  IconChecklist,
  IconChecks,
  IconCheckupList,
  IconClipboard,
  IconClipboardCheck,
  IconClipboardList,
  IconCloud,
  IconFileDescription,
  IconFiles,
  IconGauge,
  IconGavel,
  IconHammer,
  IconLayoutDashboard,
  IconReportAnalytics,
  IconSchool,
  IconPuzzle,
  IconServerCog,
  IconSettings2,
  IconShieldCheck,
  IconShieldLock,
  IconSparkles,
  IconSpeakerphone,
  IconStatusChange,
  IconTestPipe,
  IconTool,
  IconUrgent,
  IconUserCheck,
  IconUsers,
  IconUsersGroup,
  IconVideo,
  IconWorld,
} from '@tabler/icons-react';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import type {
  DirectoryCategory,
  SidebarLinkItemProps,
} from 'src/app/layout/Sidebar/components/SidebarLinks';
import {AssistantCustomOperations} from 'src/shared/auth/permissions/customOperations';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export const SidebarLinkDashboard: SidebarLinkItemProps = {
  name: 'Dashboard',
  path: PathUtility.Dashboard(),
  icon: <IconGauge />,
};

export const SidebarLinkBulletins: SidebarLinkItemProps = {
  name: 'Bulletins',
  path: PathUtility.BulletinDashboard('current'),
  validActivePaths: [
    PathUtility.BulletinDashboard('current'),
    PathUtility.BulletinDashboard('manage'),
    PathUtility.BulletinDashboard('qr-code'),
  ],
  icon: <IconCheckupList />,
  featureFlag: FeatureFlagEnum.Bulletins,
};

export const SidebarLinkForms: SidebarLinkItemProps = {
  name: 'Forms',
  path: PathUtility.FormsDashboard('user'),
  validActivePaths: [
    PathUtility.FormsDashboard('user'),
    PathUtility.FormsDashboard('manage'),
  ],
  icon: <IconClipboard />,
  featureFlag: FeatureFlagEnum.Forms,
};

export const SidebarLinkActions: SidebarLinkItemProps = {
  name: 'Actions',
  path: PathUtility.Actions('user'),
  validActivePaths: [
    PathUtility.Actions('user'),
    PathUtility.Actions('manage'),
    PathUtility.Actions('supervisor-approval'),
  ],
  icon: <IconClipboardCheck />,
  featureFlag: FeatureFlagEnum.Actions,
};

export const SidebarLinkApprovals: SidebarLinkItemProps = {
  name: 'Approvals',
  path: PathUtility.Approvals(),
  validActivePaths: [
    PathUtility.TasksDashboard(TaskTypeEnum.Approval, 'user'),
    PathUtility.TasksDashboard(TaskTypeEnum.Approval, 'manage'),
    PathUtility.TasksDashboard(TaskTypeEnum.Approval, 'supervisor-approval'),
  ],
  icon: <IconChecks />,
  featureFlag: FeatureFlagEnum.Approvals,
};

export const SidebarLinkToolboxTalks: SidebarLinkItemProps = {
  name: 'Toolbox Talks',
  path: PathUtility.ToolboxTalk(),
  validActivePaths: [
    PathUtility.TasksDashboard(TaskTypeEnum.ToolboxTalk, 'user'),
    PathUtility.TasksDashboard(TaskTypeEnum.ToolboxTalk, 'manage'),
    PathUtility.TasksDashboard(TaskTypeEnum.ToolboxTalk, 'supervisor-approval'),
  ],
  icon: <IconSpeakerphone />,
  featureFlag: FeatureFlagEnum.ToolboxTalks,
};

export const SidebarLinkTrainingCompliance: SidebarLinkItemProps = {
  name: 'Compliance',
  path: PathUtility.TrainingCompliance(),
  icon: <IconShieldCheck />,
  featureFlag: FeatureFlagEnum.Training,
};

export const SidebarLinkTrainingSchedule: SidebarLinkItemProps = {
  name: 'Schedule',
  path: PathUtility.TrainingSchedule(),
  icon: <IconCalendar />,
  featureFlag: FeatureFlagEnum.Training,
};

export const SidebarLinkTrainingSignOffs: SidebarLinkItemProps = {
  name: 'Sign-offs',
  path: PathUtility.TrainingSignOffs(),
  icon: <IconClipboardCheck />,
  featureFlag: FeatureFlagEnum.Training,
  module: ModuleEnum.Training,
};

export const SidebarLinkTrainingCourses: SidebarLinkItemProps = {
  name: 'Courses',
  path: PathUtility.TrainingDashboard('manage'),
  icon: <IconSchool />,
  featureFlag: FeatureFlagEnum.Training,
};

export const SidebarLinkTrainingMedia: SidebarLinkItemProps = {
  name: 'Media',
  path: PathUtility.TrainingDashboard('course-video'),
  icon: <IconVideo />,
  featureFlag: FeatureFlagEnum.Training,
};

export const SidebarLinkTrainingMyTraining: SidebarLinkItemProps = {
  name: 'My Training',
  path: PathUtility.TrainingDashboard('user'),
  icon: <IconUserCheck />,
  featureFlag: FeatureFlagEnum.Training,
  allowPendingUsers: true,
};

export const SidebarLinkTraining: SidebarLinkItemProps = {
  name: 'Training',
  path: PathUtility.TrainingDashboard('user'),
  validActivePaths: [
    PathUtility.TrainingDashboard('user'),
    PathUtility.TrainingDashboard('manage'),
  ],
  icon: <IconSchool />,
  featureFlag: FeatureFlagEnum.Training,
  allowPendingUsers: true,
};

export const SidebarLinkItemTracker: SidebarLinkItemProps = {
  name: 'Item Tracker',
  path: PathUtility.ItemTrackerDashboard(),
  icon: <IconHammer />,
  featureFlag: FeatureFlagEnum.ItemTracker,
};

export const SidebarLinkDocumentManager: SidebarLinkItemProps = {
  name: 'Documents',
  path: PathUtility.DocumentsManagerList(0),
  icon: <IconFileDescription />,
  featureFlag: FeatureFlagEnum.DocumentManagement,
  module: ModuleEnum.Documents,
};

export const SidebarLinkJobHazardAnalysis: SidebarLinkItemProps = {
  name: 'Job Hazard Analysis',
  path: PathUtility.JobHazardAnalysis(),
  icon: <IconChartBar />,
  featureFlag: FeatureFlagEnum.JobHazardAnalysis,
  module: ModuleEnum.JobHazardAnalysises,
};

export const SidebarLinkAssignedForms: SidebarLinkItemProps = {
  name: 'Assigned Forms',
  path: PathUtility.AssignedForms(),
  validActivePaths: [
    PathUtility.TasksDashboard(TaskTypeEnum.Form, 'user'),
    PathUtility.TasksDashboard(TaskTypeEnum.Form, 'manage'),
    PathUtility.TasksDashboard(TaskTypeEnum.Form, 'supervisor-approval'),
  ],
  icon: <IconClipboardList />,
  featureFlag: FeatureFlagEnum.Forms,
};

export const SidebarLinkSafetyEvents: SidebarLinkItemProps = {
  name: 'Safety Events',
  path: PathUtility.SafetyEvent(),
  icon: <IconUrgent />,
  featureFlag: FeatureFlagEnum.SafetyEvents,
  module: ModuleEnum.SafetyEvent,
};

export const SidebarLinkAnalytics: SidebarLinkItemProps = {
  name: 'Analytics',
  path: PathUtility.Analytics(),
  icon: <IconReportAnalytics />,
  featureFlag: FeatureFlagEnum.Analytics,
  module: ModuleEnum.Analytics,
};

export const SidebarLinkRiskAssessment: SidebarLinkItemProps = {
  name: 'Risk Assessment',
  path: PathUtility.RiskAssessment(),
  icon: <IconReportAnalytics />,
  module: ModuleEnum.RiskAssessmentCodes,
};

export const SidebarLinkPersonnel: SidebarLinkItemProps = {
  name: 'Personnel',
  path: PathUtility.Personnel('all'),
  validActivePaths: [
    PathUtility.Personnel('all'),
    PathUtility.Personnel('employee'),
    PathUtility.Personnel('activity'),
  ],
  icon: <IconUsers />,
  module: ModuleEnum.Personnel,
};

export const SidebarLinkAssets: SidebarLinkItemProps = {
  name: 'Assets',
  path: PathUtility.Assets(),
  icon: <IconTool />,
  module: ModuleEnum.Assets,
};

export const SidebarLinkAuditLog: SidebarLinkItemProps = {
  name: 'Audit Log',
  path: PathUtility.AuditLogs(),
  icon: <IconStatusChange />,
  featureFlag: FeatureFlagEnum.AuditLogs,
  module: ModuleEnum.AuditLog,
};

export const SidebarLinkWorkflows: SidebarLinkItemProps = {
  name: 'Workflows',
  path: PathUtility.WorkflowsDashboard('nexus'),
  icon: <IconArrowsRight />,
  featureFlag: FeatureFlagEnum.Workflows,
  module: ModuleEnum.Workflows,
};

export const SidebarLinkClients: SidebarLinkItemProps = {
  name: 'Clients',
  path: PathUtility.Clients('list'),
  icon: <IconBuildingSkyscraper />,
  featureFlag: FeatureFlagEnum.Clients,
  module: ModuleEnum.Clients,
};

export const SidebarLinkInspections: SidebarLinkItemProps = {
  name: 'Inspections',
  path: PathUtility.Inspections('list'),
  icon: <IconChecklist />,
  featureFlag: FeatureFlagEnum.Inspections,
  module: ModuleEnum.Inspections,
};

export const SidebarLinkAssistant: SidebarLinkItemProps = {
  name: 'Ask Scout',
  path: PathUtility.Assistant(),
  icon: <IconSparkles />,
  featureFlag: FeatureFlagEnum.Assistant,
  module: ModuleEnum.Assistant,
};

export const SidebarLinkAssistantControlCenter: SidebarLinkItemProps = {
  name: 'Scout Usage',
  path: PathUtility.AssistantControlCenter(),
  icon: <IconGauge />,
  featureFlag: FeatureFlagEnum.Assistant,
  module: ModuleEnum.Assistant,
  operation: PermissionOperationEnum.Custom,
  operationName: AssistantCustomOperations.controlCenter,
};

export const SidebarLinkKnowledgeBase: SidebarLinkItemProps = {
  name: 'Knowledge Base',
  path: PathUtility.KnowledgeBase(),
  icon: <IconBook2 />,
  featureFlag: FeatureFlagEnum.KnowledgeBase,
  module: ModuleEnum.KnowledgeBase,
};

export const SidebarLinkComplianceRegulatory: SidebarLinkItemProps = {
  name: 'Compliance - Regulatory',
  path: PathUtility.InstanceComplianceStandards(),
  icon: <IconGavel />,
  featureFlag: FeatureFlagEnum.Compliance,
  module: ModuleEnum.Compliance,
};

export const SidebarLinkComplianceCompany: SidebarLinkItemProps = {
  name: 'Compliance - Company',
  path: PathUtility.CustomComplianceStandards(),
  icon: <IconLayoutDashboard />,
  featureFlag: FeatureFlagEnum.Compliance,
  module: ModuleEnum.Compliance,
};

export const SidebarLinkComplianceStandardsMerged: SidebarLinkItemProps = {
  name: 'Compliance Standards',
  path: PathUtility.InstanceComplianceStandards(),
  validActivePaths: [
    PathUtility.InstanceComplianceStandards(),
    PathUtility.InstanceComplianceStandards('company'),
    PathUtility.InstanceComplianceStandards('regulatory'),
    PathUtility.CustomComplianceStandards(),
  ],
  icon: <IconGavel />,
  featureFlag: FeatureFlagEnum.Compliance,
  module: ModuleEnum.Compliance,
};

export const SidebarLinkVendors: SidebarLinkItemProps = {
  name: 'Vendors',
  path: PathUtility.Vendors('list'),
  icon: <IconBuildingStore />,
  featureFlag: FeatureFlagEnum.Vendors,
  module: ModuleEnum.Vendors,
};

export const SidebarLinkUserGroupsAdmin: SidebarLinkItemProps = {
  name: 'Groups',
  path: PathUtility.UserGroup(),
  icon: <IconUsersGroup />,
  module: ModuleEnum.UserGroups,
  allowGlobalAdminBypass: true,
};

export const SidebarLinkModuleCustomization: SidebarLinkItemProps = {
  name: 'Module Customization',
  path: PathUtility.ModuleCustomization(),
  icon: <IconPuzzle />,
  module: ModuleEnum.ModuleExtensions,
  allowGlobalAdminBypass: true,
};

export const SidebarLinkAttachmentClassificationsAdmin: SidebarLinkItemProps = {
  name: 'Attachment Classifications',
  path: PathUtility.AttachmentClassification(),
  icon: <IconFiles />,
  module: ModuleEnum.AttachmentClassifications,
  allowGlobalAdminBypass: true,
};

export const SidebarLinkOsha: SidebarLinkItemProps = {
  name: 'OSHA',
  path: PathUtility.Osha(),
  icon: <IconReportAnalytics />,
  module: ModuleEnum.Osha,
  featureFlag: FeatureFlagEnum.Osha,
};

export const SidebarLinkProjectsAdmin: SidebarLinkItemProps = {
  name: 'Projects',
  path: PathUtility.Project('list'),
  icon: <IconBuilding />,
  module: ModuleEnum.Projects,
  allowGlobalAdminBypass: true,
};

export const SidebarLinkItemTrackerAdmin: SidebarLinkItemProps = {
  name: 'Item Tracker (Manage)',
  path: PathUtility.ItemDefinitions(),
  icon: <IconHammer />,
  featureFlag: FeatureFlagEnum.ItemTracker,
  module: ModuleEnum.ItemTrackerDefinitions,
};

export const SidebarLinkNotificationRules: SidebarLinkItemProps = {
  name: 'Notifications',
  path: PathUtility.NotificationRule(),
  icon: <IconBellRinging />,
  module: ModuleEnum.NotificationRules,
};

export const SidebarLinkComplianceStandards: SidebarLinkItemProps = {
  name: 'Compliance Standards',
  path: PathUtility.ComplianceStandards(),
  icon: <IconGavel />,
  superAdminOnly: true,
};

export const SidebarLinkAccounts: SidebarLinkItemProps = {
  name: 'Accounts',
  path: PathUtility.Accounts(),
  icon: <IconAddressBook />,
  superAdminOnly: true,
};

export const SidebarLinkInstances: SidebarLinkItemProps = {
  name: 'Instances',
  path: PathUtility.Instances(),
  icon: <IconWorld />,
  superAdminOnly: true,
};

export const SidebarLinkSuperAdmins: SidebarLinkItemProps = {
  name: 'Super Admins',
  path: PathUtility.SuperAdmins(),
  icon: <IconShieldLock />,
  superAdminOnly: true,
};

export const SidebarLinkTestArea: SidebarLinkItemProps = {
  name: 'Test Area',
  path: PathUtility.AdminTestArea(),
  icon: <IconTestPipe />,
  superAdminOnly: true,
};

export const SidebarLinkBackgroundJobs: SidebarLinkItemProps = {
  name: 'Background Jobs',
  path: PathUtility.AdminHangfireMonitor(),
  icon: <IconSettings2 />,
  superAdminOnly: true,
};

export const SidebarLinkCloudWatch: SidebarLinkItemProps = {
  name: 'CloudWatch',
  path: PathUtility.AdminCloudWatch(),
  icon: <IconCloud />,
  superAdminOnly: true,
};

export const SidebarLinkGlobalAssistantControlCenter: SidebarLinkItemProps = {
  name: 'Scout Usage',
  path: PathUtility.AdminAssistantControlCenter(),
  icon: <IconChartBar />,
  superAdminOnly: true,
};

export const SettingsDirectoryConfig: DirectoryCategory[] = [
  {
    title: 'Auditing',
    entries: [
      {
        link: SidebarLinkAuditLog,
        description:
          'Review who changed what and when across every module.',
      },
    ],
  },
  {
    title: 'Customization',
    entries: [
      {
        link: SidebarLinkAttachmentClassificationsAdmin,
        description:
          'Define the classification labels available when files are attached to records.',
      },
      {
        link: SidebarLinkModuleCustomization,
        description:
          'Configure extension tabs and custom fields for the built-in modules.',
      },
    ],
  },
  {
    title: 'Notifications',
    entries: [
      {
        link: SidebarLinkNotificationRules,
        description:
          'Define the rules that send email notifications when records change.',
      },
    ],
  },
  {
    title: 'Safety',
    entries: [
      {
        link: SidebarLinkComplianceStandardsMerged,
        description:
          'Browse regulatory standards and maintain company standards for this instance.',
      },
      {
        link: SidebarLinkRiskAssessment,
        description:
          'Manage the risk assessment codes used to rate hazards across modules.',
      },
    ],
  },
];

export const SidebarLinkSettings: SidebarLinkItemProps = {
  name: 'Settings',
  path: PathUtility.Settings(),
  icon: <IconAdjustments />,
  directory: SettingsDirectoryConfig,
};

export const SystemDirectoryConfig: DirectoryCategory[] = [
  {
    title: 'Tenants',
    entries: [
      {
        link: SidebarLinkInstances,
        description:
          'Create, clone, configure, and delete customer instances and their feature flags.',
      },
      {
        link: SidebarLinkAccounts,
        description: 'Look up user accounts across all instances.',
      },
    ],
  },
  {
    title: 'Access',
    entries: [
      {
        link: SidebarLinkSuperAdmins,
        description: 'Grant or revoke platform super administrator access.',
      },
    ],
  },
  {
    title: 'Reference Data',
    entries: [
      {
        link: SidebarLinkComplianceStandards,
        description:
          'Maintain the regulatory compliance standards library shared by every instance.',
      },
    ],
  },
  {
    title: 'Monitoring',
    entries: [
      {
        link: SidebarLinkBackgroundJobs,
        description:
          'Live view of Hangfire queue depths, servers, and job history.',
      },
      {
        link: SidebarLinkGlobalAssistantControlCenter,
        description: 'Platform-wide Scout token usage, cost, and activity.',
      },
      {
        link: SidebarLinkCloudWatch,
        description:
          'Alarms, metrics, and ECS application logs pulled live from CloudWatch.',
      },
    ],
  },
  {
    title: 'Developer Tools',
    entries: [
      {
        link: SidebarLinkTestArea,
        description:
          'Demonstration and diagnostic pages for the form engine, API errors, and offline sessions.',
      },
    ],
  },
];

export const SidebarLinkSystem: SidebarLinkItemProps = {
  name: 'System',
  path: PathUtility.AdminSystem(),
  icon: <IconServerCog />,
  superAdminOnly: true,
  directory: SystemDirectoryConfig,
};

export const SidebarUserLinksConfig: SidebarLinkItemProps[] = [
  SidebarLinkDashboard,
  SidebarLinkAssistant,
  SidebarLinkBulletins,
  SidebarLinkActions,
  SidebarLinkApprovals,
  SidebarLinkAssignedForms,
  SidebarLinkDocumentManager,
  SidebarLinkForms,
  SidebarLinkItemTracker,
  SidebarLinkJobHazardAnalysis,
  SidebarLinkPersonnel,
  SidebarLinkToolboxTalks,
  SidebarLinkTraining,
  SidebarLinkAnalytics,
];

export const SidebarInstanceSettingsLinksConfig: SidebarLinkItemProps[] = [
  SidebarLinkAssets,
  SidebarLinkClients,
  SidebarLinkInspections,
  SidebarLinkItemTrackerAdmin,
  SidebarLinkKnowledgeBase,
  SidebarLinkOsha,
  SidebarLinkProjectsAdmin,
  SidebarLinkSafetyEvents,
  SidebarLinkAssistantControlCenter,
  SidebarLinkSettings,
  SidebarLinkUserGroupsAdmin,
  SidebarLinkVendors,
  SidebarLinkWorkflows,
];

export const SidebarGlobalSettingsLinksConfig: SidebarLinkItemProps[] = [
  SidebarLinkSystem,
];
